'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';

import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

interface NavItem {
  id: string;
  label: string;
  href: string;
  icon: string;
  badge?: number;
  badgeType?: 'danger' | 'warning' | 'info';
}

interface NavGroup {
  id: string;
  label: string;
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    id: 'main',
    label: 'Main',
    items: [
      { id: 'nav-dashboard', label: 'Dashboard', href: '/artist-dashboard', icon: 'HomeIcon' },
      { id: 'nav-releases', label: 'Releases', href: '/releases', icon: 'MusicalNoteIcon' },
      { id: 'nav-create', label: 'Create Release', href: '/create-release', icon: 'PlusCircleIcon' },
    ],
  },
  {
    id: 'insights',
    label: 'Insights',
    items: [
      { id: 'nav-analytics', label: 'Analytics', href: '/analytics', icon: 'ChartBarIcon' },
      { id: 'nav-dsp-status', label: 'DSP Status', href: '/dsp-status', icon: 'GlobeAltIcon' },
      { id: 'nav-royalties', label: 'Royalties', href: '/royalties', icon: 'BanknotesIcon' },
      { id: 'nav-payouts', label: 'Payouts', href: '/payouts', icon: 'CreditCardIcon' },
    ],
  },
  {
    id: 'account',
    label: 'Account',
    items: [
      { id: 'nav-settings', label: 'Settings', href: '/settings', icon: 'Cog6ToothIcon' },
      { id: 'nav-plans', label: 'Plans & Billing', href: '/plans', icon: 'StarIcon' },
      { id: 'nav-legal', label: 'Legal & Compliance', href: '/legal-documents', icon: 'DocumentTextIcon' },
      { id: 'nav-support', label: 'Support', href: '/support', icon: 'LifebuoyIcon' },
    ],
  },
];

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export default function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const pathname = usePathname();
  const router = useRouter();
  const { user } = useAuth();
  const supabase = createClient();

  const [profile, setProfile] = useState<any>(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);

  // Fetch user profile from DB
  useEffect(() => {
    if (!user?.id) return;
    supabase
      .from('user_profiles')
      .select('full_name, artist_name, role, subscription_plan')
      .eq('id', user.id)
      .single()
      .then(({ data }) => {
        if (data) setProfile(data);
      });
  }, [user?.id]);

  // Close popup on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        setPopupOpen(false);
      }
    };
    if (popupOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [popupOpen]);

  // Derive display name
  const displayName =
    profile?.full_name ||
    profile?.artist_name ||
    user?.user_metadata?.full_name ||
    user?.user_metadata?.name ||
    user?.email?.split('@')[0] ||
    'Artist';

  // Derive initials
  const initials = displayName
    .split(' ')
    .slice(0, 2)
    .map((w: string) => w[0]?.toUpperCase() || '')
    .join('') || 'A';

  // Derive plan label
  const planMap: Record<string, string> = {
    free: 'Free Plan',
    pro: 'Pro Plan',
    label: 'Label Plan',
  };
  const planLabel = planMap[(profile?.subscription_plan || '').toLowerCase()] || 'Free Plan';

  // Admin check
  const role = profile?.role || user?.user_metadata?.role || '';
  const isAdmin = role === 'SUPER_ADMIN' || role === 'ADMIN';

  const handleSignOut = async () => {
    setPopupOpen(false);
    await supabase.auth.signOut();
    router.push('/');
  };

  const isActive = (href: string, id: string) => {
    if (id === 'nav-dashboard' && (pathname === '/artist-dashboard' || pathname === '/')) return true;
    if (id === 'nav-releases' && (pathname === '/releases' || pathname.startsWith('/release/'))) return true;
    if (id === 'nav-create' && pathname === '/create-release') return true;
    if (id === 'nav-catalog' && pathname === '/catalog') return true;
    if (id === 'nav-analytics' && pathname === '/analytics') return true;
    if (id === 'nav-dsp-status' && pathname === '/dsp-status') return true;
    if (id === 'nav-royalties' && (pathname === '/royalties' || pathname === '/artist-royalties')) return true;
    if (id === 'nav-payouts' && pathname === '/payouts') return true;
    if (id === 'nav-distribution' && pathname === '/distribution') return true;
    if (id === 'nav-settings' && pathname === '/settings') return true;
    if (id === 'nav-plans' && pathname === '/plans') return true;
    if (id === 'nav-legal' && pathname === '/legal-documents') return true;
    if (id === 'nav-support' && pathname === '/support') return true;
    return false;
  };

  const getBadgeClass = (type?: 'danger' | 'warning' | 'info') => {
    switch (type) {
      case 'danger': return 'bg-danger text-white';
      case 'warning': return 'bg-warning text-black';
      case 'info': return 'bg-info text-white';
      default: return 'bg-primary text-white';
    }
  };

  return (
    <aside
      className="fixed left-0 top-0 h-full z-40 flex flex-col scrollbar-thin overflow-y-auto"
      style={{
        width: collapsed ? '64px' : '240px',
        background: 'var(--card)',
        borderRight: '1px solid var(--border)',
        transition: 'width 300ms ease',
      }}
    >
      {/* Logo */}
      <div
        className="flex items-center h-16 px-4 flex-shrink-0"
        style={{ borderBottom: '1px solid var(--border)' }}
      >
        <div className="flex items-center min-w-0">
          <img
            src="/assets/images/Untitled__5000_x_1500_px__1_-1787297230471.png"
            alt="TubeNexa"
            className="object-contain flex-shrink-0"
            style={{ height: '36px', width: 'auto' }}
          />
        </div>
        {!collapsed && (
          <button
            onClick={onToggle}
            className="ml-auto btn-ghost p-1.5 rounded-md"
            aria-label="Collapse sidebar"
          >
            <Icon name="ChevronLeftIcon" size={16} />
          </button>
        )}
      </div>

      {/* Collapsed toggle */}
      {collapsed && (
        <button
          onClick={onToggle}
          className="flex items-center justify-center h-10 mt-2 mx-2 btn-ghost rounded-md"
          aria-label="Expand sidebar"
        >
          <Icon name="ChevronRightIcon" size={16} />
        </button>
      )}

      {/* Nav Groups */}
      <nav className="flex-1 py-4 px-2">
        {navGroups.map((group) => (
          <div key={group.id} className="mb-5">
            {!collapsed && (
              <p
                className="text-2xs font-semibold uppercase tracking-widest mb-2 px-3"
                style={{ color: 'var(--muted-foreground)' }}
              >
                {group.label}
              </p>
            )}
            {group.items.map((item) => {
              const active = isActive(item.href, item.id);
              return (
                <Link
                  key={item.id}
                  href={item.href}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg mb-0.5 relative group sidebar-hover ${
                    active ? 'sidebar-active' : ''
                  }`}
                  title={collapsed ? item.label : undefined}
                >
                  <div className="relative flex-shrink-0">
                    <Icon
                      name={item.icon as any}
                      size={18}
                      className={active ? 'text-primary-light' : 'text-muted-foreground'}
                    />
                    {item.badge && collapsed && (
                      <span
                        className={`absolute -top-1 -right-1 text-2xs font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-0.5 ${getBadgeClass(item.badgeType)}`}
                      >
                        {item.badge}
                      </span>
                    )}
                  </div>
                  {!collapsed && (
                    <>
                      <span
                        className={`text-sm font-medium flex-1 truncate ${
                          active ? 'text-primary-light' : 'text-secondary-foreground'
                        }`}
                      >
                        {item.label}
                      </span>
                      {item.badge && (
                        <span
                          className={`text-2xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 ${getBadgeClass(item.badgeType)}`}
                        >
                          {item.badge}
                        </span>
                      )}
                    </>
                  )}
                  {/* Tooltip for collapsed */}
                  {collapsed && (
                    <div
                      className="absolute left-full ml-3 px-2.5 py-1.5 rounded-md text-sm font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-50"
                      style={{
                        background: 'var(--card-elevated)',
                        border: '1px solid var(--border-light)',
                        color: 'var(--foreground)',
                        transition: 'opacity 0.15s ease',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                      }}
                    >
                      {item.label}
                    </div>
                  )}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      {/* User footer */}
      <div
        className="flex-shrink-0 p-3 relative"
        style={{ borderTop: '1px solid var(--border)' }}
        ref={popupRef}
      >
        {/* Admin panel link — only for ADMIN / SUPER_ADMIN */}
        {isAdmin && (
          <Link
            href="/admin/login"
            className={`flex items-center gap-3 px-3 py-2 rounded-lg mb-1 sidebar-hover ${collapsed ? 'justify-center' : ''}`}
            title={collapsed ? 'Admin Panel' : undefined}
          >
            <div
              className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0"
              style={{ background: 'rgba(239,68,68,0.12)' }}
            >
              <Icon name="ShieldCheckIcon" size={14} style={{ color: '#ef4444' }} />
            </div>
            {!collapsed && (
              <span className="text-xs font-medium" style={{ color: '#ef4444' }}>
                Admin Panel
              </span>
            )}
          </Link>
        )}

        {/* Profile card — clickable to open popup */}
        <div
          className={`flex items-center gap-3 px-2 py-2 rounded-lg cursor-pointer sidebar-hover ${collapsed ? 'justify-center' : ''}`}
          onClick={() => setPopupOpen((prev) => !prev)}
          title={collapsed ? displayName : undefined}
        >
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold"
            style={{ background: 'var(--primary)', color: 'white' }}
          >
            {initials}
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate" style={{ color: 'var(--foreground)' }}>
                {displayName}
              </p>
              <p className="text-2xs truncate" style={{ color: 'var(--muted-foreground)' }}>
                {planLabel}
              </p>
            </div>
          )}
          {!collapsed && (
            <Icon name="Cog6ToothIcon" size={16} className="text-muted-foreground flex-shrink-0" />
          )}
        </div>

        {/* Profile popup menu */}
        {popupOpen && (
          <div
            className="absolute bottom-full left-3 right-3 mb-2 rounded-xl overflow-hidden z-50"
            style={{
              background: 'var(--card-elevated)',
              border: '1px solid var(--border-light)',
              boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
            }}
          >
            {/* User info header */}
            <div className="px-4 py-3" style={{ borderBottom: '1px solid var(--border)' }}>
              <p className="text-sm font-semibold truncate" style={{ color: 'var(--foreground)' }}>
                {displayName}
              </p>
              <p className="text-xs truncate mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
                {user?.email || ''}
              </p>
            </div>

            {/* Menu items */}
            <div className="py-1">
              <Link
                href="/settings"
                onClick={() => setPopupOpen(false)}
                className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-white/5 transition-colors"
                style={{ color: 'var(--foreground)' }}
              >
                <Icon name="Cog6ToothIcon" size={15} className="text-muted-foreground" />
                Account Settings
              </Link>
              <Link
                href="/plans"
                onClick={() => setPopupOpen(false)}
                className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-white/5 transition-colors"
                style={{ color: 'var(--foreground)' }}
              >
                <Icon name="StarIcon" size={15} className="text-muted-foreground" />
                Subscription Plan
              </Link>
            </div>

            {/* Sign out */}
            <div style={{ borderTop: '1px solid var(--border)' }} className="py-1">
              <button
                onClick={handleSignOut}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-red-500/10 transition-colors"
                style={{ color: '#ef4444' }}
              >
                <Icon name="ArrowRightOnRectangleIcon" size={15} style={{ color: '#ef4444' }} />
                Sign Out
              </button>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}